// Documentos do aluno — arquivados no storage + envios da família aguardando conferência.
import React, { useCallback, useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, Image as ImageIcon, Loader2, Send, Sparkles, Copy, Inbox, Wand2, FileSearch, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import SmartImportModal from '@/pages/Matriculas/SmartImportModal';
import { docsHeadline } from '@/pages/Matriculas/EnrollmentFilesOverview';

const fmt = (d) => (d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—');

const DOC_STATUS_LABEL = {
  enviado: 'Enviado', em_analise: 'Em análise', conferido: 'Conferido',
  aprovado: 'Aprovado', rejeitado: 'Rejeitado', substituido: 'Substituído',
};
const DOC_STATUS_CLS = {
  enviado: 'dna-tone-info',
  em_analise: 'dna-tone-warning',
  conferido: 'dna-tone-success',
  aprovado: 'dna-tone-success',
  rejeitado: 'dna-tone-danger',
  substituido: 'dna-tone-neutral',
};

const STATUS_DOT = {
  aprovado: 'dna-solid-success', recebido_fisico: 'dna-solid-success',
  enviado: 'dna-solid-info', em_analise: 'dna-solid-info',
  entrega_posterior: 'dna-solid-info', pendente: 'dna-solid-warning',
  rejeitado: 'dna-solid-danger', nao_enviado: 'dna-solid-neutral',
};

export default function DocumentosCard({ studentId, studentName, onReviewed }) {
  const [docs, setDocs] = useState([]);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(null);
  const [chk, setChk] = useState(null);
  const [origins, setOrigins] = useState(null);
  const [statusBusy, setStatusBusy] = useState('');
  const [rejecting, setRejecting] = useState(null);
  const [motivo, setMotivo] = useState('');

  const mudarStatus = async (d, status, note) => {
    if (status === 'rejeitado' && !(note || '').trim()) {
      toast.error('Informe o motivo da rejeição.');
      return;
    }
    setStatusBusy(d.id);
    try {
      await api.post(`/smart-import/documents/${d.id}/status`, { status, note: note || null });
      toast.success(status === 'aprovado' ? 'Documento aprovado.' : 'Documento rejeitado.');
      setRejecting(null); setMotivo('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível atualizar o status.');
    } finally { setStatusBusy(''); }
  };

  const carregarOrigens = async () => {
    try {
      const { data } = await api.get(`/smart-import/origins/student/${studentId}`);
      setOrigins(data.items || []);
    } catch (_) { toast.error('Não foi possível carregar a origem dos dados.'); }
  };

  const load = useCallback(async () => {
    if (!studentId) return;
    try {
      const [d, f, y] = await Promise.all([
        api.get(`/smart-import/students/${studentId}/documents`),
        api.get(`/smart-import/students/${studentId}/family-uploads`),
        api.get(`/enrollment-docs/students/${studentId}/years`),
      ]);
      setDocs(d.data.items || []);
      setPending(f.data.pending || []);
      const ys = y.data.years || [];
      setYears(ys);
      setYear((cur) => cur || ys[0]);
    } catch (_) { /* silencioso */ }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!studentId || !year) return;
    api.get(`/enrollment-docs/students/${studentId}/checklist`, { params: { year } })
      .then(({ data }) => setChk(data)).catch(() => setChk(null));
  }, [studentId, year]);

  const openOriginal = async (doc) => {
    try {
      const { data } = await api.get(`/smart-import/documents/${doc.id}/file`, { responseType: 'blob' });
      window.open(URL.createObjectURL(data), '_blank');
    } catch (_) { toast.error('Arquivo original indisponível.'); }
  };

  const pedirDocumentos = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/enrollment-docs/link', { student_id: studentId, year });
      await navigator.clipboard?.writeText(data.link).catch(() => {});
      window.open(data.whatsapp_url, '_blank');
      toast.success('Link oficial de documentos copiado. Escolha o contato no WhatsApp.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível gerar o link.');
    } finally { setBusy(false); }
  };

  const copiarLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/enrollment-docs/link', { student_id: studentId, year });
      await navigator.clipboard?.writeText(data.link).catch(() => {});
      toast.success(`Link copiado: ${data.link}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível gerar o link.');
    } finally { setBusy(false); }
  };

  const completarCadastro = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/smart-import/enrollment-docs/analyze',
        { student_id: studentId, year });
      setReview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível ler os documentos.');
    } finally { setBusy(false); }
  };

  const classify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/enrollment-docs/students/${studentId}/classify`, { year });
      setChk(data.checklist);
      toast.success(`${data.classified} arquivo(s) classificado(s) pela IA.`);
      if (data.unclassified) toast.warning(`${data.unclassified} não reconhecido(s) pela IA.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível classificar com a IA.');
    } finally { setBusy(false); }
  };

  const conferirComIA = async () => {    setBusy(true);
    try {
      const { data } = await api.post('/smart-import/family-uploads/analyze', { student_id: studentId });
      setReview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao ler os documentos enviados.');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="documentos-card">
      {!!pending.length && (
        <div className="mb-3 p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="envios-familia">
          <p className="text-[13px] font-semibold text-amber-900 flex items-center gap-2">
            <Inbox className="h-4 w-4" /> Envios da família · {pending.length} aguardando conferência
          </p>
          <Button size="sm" className="mt-2 bg-violet-600 hover:bg-violet-700 text-white"
            disabled={busy} onClick={conferirComIA} data-testid="btn-conferir-envios">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
            Conferir com IA
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Button size="sm" variant="outline" disabled={busy} onClick={pedirDocumentos}
          data-testid="btn-pedir-documentos" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
          <Send className="h-4 w-4 mr-1.5" /> Pedir por WhatsApp
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={copiarLink}
          data-testid="btn-copiar-link-docs" className="border-violet-200 text-violet-700 hover:bg-violet-50">
          <Copy className="h-4 w-4 mr-1.5" /> Copiar link de documentos
        </Button>
        <Button size="sm" variant="ghost" onClick={load} data-testid="btn-reload-docs" className="text-slate-500">
          Atualizar
        </Button>
      </div>

      {!!chk && (
        <div className="mb-3 rounded-xl border border-slate-100 p-3" data-testid="checklist-matricula">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[12px] font-semibold text-slate-700" data-testid="checklist-headline">
              {docsHeadline(chk.summary)}
            </p>
            {years.length > 1 && (
              <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                data-testid="checklist-year" className="text-[12px] border rounded-md px-1.5 py-0.5">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
          </div>
          {!!chk.summary.unclassified && (
            <Button size="sm" disabled={busy} onClick={classify} data-testid="btn-classificar-ia"
              className="mb-2 w-full bg-violet-600 hover:bg-violet-700 text-white">
              <Sparkles className="h-4 w-4 mr-1.5" />
              Classificar {chk.summary.unclassified} arquivo(s) com IA
            </Button>
          )}
          {chk.items.some((i) => i.files?.length) && (
            <Button size="sm" disabled={busy} onClick={completarCadastro} data-testid="btn-completar-cadastro"
              className="mb-2 w-full bg-teal-700 hover:bg-teal-800 text-white">
              <Wand2 className="h-4 w-4 mr-1.5" /> Completar cadastro com os documentos
            </Button>
          )}
          <div className="space-y-1">
            {chk.items.filter((i) => i.group !== 'outros').map((i) => (
              <div key={i.key} className="flex items-center gap-2 text-[12px]" data-testid={`chk-${i.key}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[i.status] || 'bg-slate-300'}`} />
                <span className="flex-1 truncate text-slate-600">{i.label}</span>
                {!!i.files?.length && <span className="text-[10.5px] text-violet-600" data-testid={`chk-${i.key}-files`}>{i.files.length} arquivo(s)</span>}
                {!i.files?.length && !!i.covered_by?.length && <span className="text-[10.5px] text-teal-700" data-testid={`chk-${i.key}-covered`}>no arquivo de {chk.items.find((x) => x.key === i.covered_by[0].from_type)?.label || 'outro documento'}</span>}
                <span className="text-[11px] text-slate-400">{i.status_label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {docs.length === 0 ? (
        <p className="text-[12px] text-slate-400 text-center py-4">Nenhum documento arquivado</p>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {docs.map((d) => (
            <div key={d.id} className="rounded-lg border border-slate-100" data-testid={`doc-row-${d.id}`}>
              <button onClick={() => openOriginal(d)} data-testid={`doc-item-${d.id}`}
                className="w-full flex items-center gap-2 px-2.5 py-2 rounded-t-lg hover:bg-slate-50 transition-colors text-left">
                {(d.content_type || '').includes('pdf')
                  ? <FileText className="h-4 w-4 text-rose-500 flex-shrink-0" />
                  : <ImageIcon className="h-4 w-4 text-blue-500 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-slate-700 truncate">{d.doc_type_label || 'Documento'}</p>
                  <p className="text-[11px] text-slate-400 truncate">{d.filename} · {fmt(d.created_at)}</p>
                </div>
                <span className="flex items-center gap-1 flex-shrink-0">
                  {d.source === 'family_upload' && (
                    <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">Família</Badge>
                  )}
                  {!!d.status && (
                    <Badge variant="outline" className={`text-[10px] ${DOC_STATUS_CLS[d.status] || 'bg-slate-50 text-slate-600 border-slate-200'}`}
                      data-testid={`doc-status-${d.id}`}>
                      {DOC_STATUS_LABEL[d.status] || d.status}
                    </Badge>
                  )}
                  {d.review_status === 'pending' && !d.status && (
                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">A conferir</Badge>
                  )}
                </span>
              </button>

              <div className="flex flex-wrap items-center gap-1.5 px-2.5 pb-2">
                <Button size="sm" variant="ghost" disabled={statusBusy === d.id || d.status === 'aprovado'}
                  className="h-6 px-2 text-[10.5px] text-emerald-700 hover:bg-emerald-50"
                  data-testid={`doc-approve-${d.id}`}
                  onClick={() => mudarStatus(d, 'aprovado')}>
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Aprovar
                </Button>
                <Button size="sm" variant="ghost" disabled={statusBusy === d.id}
                  className="h-6 px-2 text-[10.5px] text-rose-700 hover:bg-rose-50"
                  data-testid={`doc-reject-${d.id}`}
                  onClick={() => setRejecting(rejecting === d.id ? null : d.id)}>
                  <XCircle className="h-3 w-3 mr-1" /> Rejeitar
                </Button>
                {!!d.status_history?.length && (
                  <span className="text-[10px] text-slate-400">
                    {DOC_STATUS_LABEL[d.status] || 'Enviado'} em {fmt(d.status_history[d.status_history.length - 1].at)}
                    {d.status_history[d.status_history.length - 1].note ? ` · ${d.status_history[d.status_history.length - 1].note}` : ''}
                  </span>
                )}
              </div>

              {rejecting === d.id && (
                <div className="flex flex-wrap items-center gap-1.5 px-2.5 pb-2" data-testid={`doc-reject-form-${d.id}`}>
                  <Input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                    placeholder="Motivo da rejeição (obrigatório)" className="h-7 text-[11.5px] flex-1 min-w-[180px]"
                    data-testid={`doc-reject-reason-${d.id}`} />
                  <Button size="sm" className="h-7 text-[11px] bg-rose-600 hover:bg-rose-700 text-white"
                    disabled={statusBusy === d.id} data-testid={`doc-reject-confirm-${d.id}`}
                    onClick={() => mudarStatus(d, 'rejeitado', motivo)}>
                    Confirmar rejeição
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Origem dos dados: qual documento preencheu cada campo do cadastro */}
      <div className="mt-3">
        <Button size="sm" variant="ghost" className="text-[11.5px] text-violet-700 hover:bg-violet-50"
          data-testid="btn-ver-origem" onClick={carregarOrigens}>
          <FileSearch className="h-3.5 w-3.5 mr-1.5" /> {origins ? 'Recarregar origem dos dados' : 'Ver origem dos dados'}
        </Button>
        {!!origins && (
          <div className="mt-2 rounded-xl border divide-y" data-testid="origens-list">
            {origins.length === 0 ? (
              <p className="text-[12px] text-slate-400 p-3">
                Nenhum campo deste cadastro foi preenchido por documento ainda.
              </p>
            ) : origins.map((o) => (
              <div key={`${o.field}-${o.id}`} className="px-3 py-2 text-[12px]" data-testid={`origem-${o.field}`}>
                <p className="font-semibold text-slate-700">{o.label}: <span className="font-normal">{o.value}</span></p>
                <p className="text-[11px] text-slate-500">
                  Origem: {o.filename || o.doc_type_label || '—'} · página {o.page || 1} · conferido em {fmt(o.confirmed_at)}
                  {o.manual_edit ? ' · ajustado manualmente' : ''}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <SmartImportModal
        open={!!review}
        onOpenChange={(v) => { if (!v) setReview(null); }}
        studentId={studentId}
        studentName={studentName}
        initialResult={review}
        onApplied={() => { load(); onReviewed && onReviewed(); }}
      />
    </div>
  );
}
